import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, Users, Target, Send, MapPin } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#14B8A6', '#1E3A5F', '#0D9488', '#2C5282', '#4A4A4A'];

const ImpactDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [csrProjects, setCSRProjects] = useState([]);
  const [csrSummary, setCSRSummary] = useState(null);
  const [esgMetrics, setESGMetrics] = useState([]);
  const [esgSummary, setESGSummary] = useState(null);
  const [sentiments, setSentiments] = useState([]);
  const [sentimentSummary, setSentimentSummary] = useState(null);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('xchange_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [projectsRes, csrSumRes, metricsRes, esgSumRes, sentiRes, sentiSumRes] = await Promise.all([
        axios.get(`${API}/csr/projects`, config),
        axios.get(`${API}/csr/summary`, config),
        axios.get(`${API}/esg/metrics`, config),
        axios.get(`${API}/esg/summary`, config),
        axios.get(`${API}/stakeholder/sentiments`, config),
        axios.get(`${API}/stakeholder/summary`, config)
      ]);

      setCSRProjects(projectsRes.data);
      setCSRSummary(csrSumRes.data);
      setESGMetrics(metricsRes.data);
      setESGSummary(esgSumRes.data);
      setSentiments(sentiRes.data);
      setSentimentSummary(sentiSumRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleAIQuery = async () => {
    if (!aiQuery.trim()) return;
    
    setAiLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('xchange_token');
      const response = await axios.post(
        `${API}/ai/query`,
        { query: aiQuery },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiResponse(response.data.response);
    } catch (error) {
      toast.error('AI query failed');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Prepare chart data
  const csrByCategory = csrProjects.reduce((acc, project) => {
    const existing = acc.find(item => item.name === project.category);
    if (existing) {
      existing.value += project.utilized_budget;
    } else {
      acc.push({ name: project.category, value: project.utilized_budget });
    }
    return acc;
  }, []);

  const utilizationData = csrProjects.map(p => ({
    name: p.name.substring(0, 20) + '...',
    utilized: (p.utilized_budget / p.allocated_budget * 100).toFixed(1)
  }));

  const esgCategoryData = Object.entries(esgSummary?.categories || {}).map(([name, data]) => ({
    name,
    performance: ((data.on_track / data.total) * 100).toFixed(1)
  }));

  const sdgData = csrProjects.flatMap(p => p.sdg_goals).reduce((acc, sdg) => {
    const existing = acc.find(item => item.sdg === `SDG ${sdg}`);
    if (existing) {
      existing.count += 1;
    } else {
      acc.push({ sdg: `SDG ${sdg}`, count: 1 });
    }
    return acc;
  }, []);

  return (
    <div className="min-h-screen" style={{background: '#F8F7F4'}}>
      <Header />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>Impact Intelligence Dashboard</h1>
            <p className="text-lg" style={{fontFamily: 'Source Sans Pro, sans-serif', color: '#6B6B6B'}}>Real-time CSR and ESG performance tracking</p>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div data-testid="total-allocated-card" className="bg-white p-6 rounded-xl shadow-sm border" style={{borderColor: '#E0DED9'}}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{color: '#6B6B6B'}}>Total Allocated</span>
                <BarChart3 style={{color: '#D1A85B'}} size={20} />
              </div>
              <div className="text-3xl font-bold" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>
                ₹{(csrSummary?.total_allocated / 10000000).toFixed(1)}Cr
              </div>
              <div className="text-sm mt-1" style={{color: '#6B6B6B'}}>{csrSummary?.total_projects} projects</div>
            </div>

            <div data-testid="utilization-card" className="bg-white p-6 rounded-xl shadow-sm border" style={{borderColor: '#E0DED9'}}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{color: '#6B6B6B'}}>Utilization Rate</span>
                <TrendingUp style={{color: '#D1A85B'}} size={20} />
              </div>
              <div className="text-3xl font-bold" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>
                {csrSummary?.utilization_rate.toFixed(1)}%
              </div>
              <div className="text-sm mt-1" style={{color: '#D1A85B'}}>On track</div>
            </div>

            <div data-testid="beneficiaries-card" className="bg-white p-6 rounded-xl shadow-sm border" style={{borderColor: '#E0DED9'}}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{color: '#6B6B6B'}}>Beneficiaries</span>
                <Users style={{color: '#D1A85B'}} size={20} />
              </div>
              <div className="text-3xl font-bold" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>
                {(csrSummary?.total_beneficiaries / 1000).toFixed(1)}K
              </div>
              <div className="text-sm mt-1" style={{color: '#6B6B6B'}}>Lives impacted</div>
            </div>

            <div data-testid="sentiment-card" className="bg-white p-6 rounded-xl shadow-sm border" style={{borderColor: '#E0DED9'}}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{color: '#6B6B6B'}}>Positive Sentiment</span>
                <Target style={{color: '#D1A85B'}} size={20} />
              </div>
              <div className="text-3xl font-bold" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>
                {sentimentSummary?.positive_rate.toFixed(1)}%
              </div>
              <div className="text-sm mt-1" style={{color: '#6B6B6B'}}>{sentimentSummary?.total} responses</div>
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* CSR by Category */}
            <div data-testid="csr-category-chart" className="bg-white p-5 rounded-xl shadow-sm border" style={{borderColor: '#E0DED9'}}>
              <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>CSR Fund Distribution</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={csrByCategory}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => entry.name}
                    outerRadius={75}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {csrByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => `₹${(value / 1000000).toFixed(2)}M`}
                    contentStyle={{fontSize: '12px'}}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Project Utilization */}
            <div data-testid="utilization-chart" className="bg-white p-5 rounded-xl shadow-sm border" style={{borderColor: '#E0DED9'}}>
              <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>Project Utilization Rate</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={utilizationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0DED9" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} style={{fontSize: '11px'}} />
                  <YAxis style={{fontSize: '11px'}} />
                  <Tooltip contentStyle={{fontSize: '12px'}} />
                  <Bar dataKey="utilized" fill="#D1A85B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* ESG Performance */}
            <div data-testid="esg-performance-chart" className="bg-white p-5 rounded-xl shadow-sm border" style={{borderColor: '#E0DED9'}}>
              <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>ESG Performance by Category</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={esgCategoryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0DED9" />
                  <XAxis dataKey="name" style={{fontSize: '11px'}} />
                  <YAxis style={{fontSize: '11px'}} />
                  <Tooltip contentStyle={{fontSize: '12px'}} />
                  <Bar dataKey="performance" fill="#D1A85B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* SDG Alignment */}
            <div data-testid="sdg-alignment-chart" className="bg-white p-5 rounded-xl shadow-sm border" style={{borderColor: '#E0DED9'}}>
              <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>SDG Alignment</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={sdgData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0DED9" />
                  <XAxis dataKey="sdg" style={{fontSize: '11px'}} />
                  <YAxis style={{fontSize: '11px'}} />
                  <Tooltip contentStyle={{fontSize: '12px'}} />
                  <Bar dataKey="count" fill="#D1A85B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* CSR Projects Table */}
          <div data-testid="csr-projects-table" className="bg-white rounded-xl shadow-sm border p-5 mb-6" style={{borderColor: '#E0DED9'}}>
            <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>Active CSR Projects</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{borderBottom: '1px solid #E0DED9'}}>
                    <th className="text-left py-2 px-3 text-xs font-semibold" style={{color: '#3A3F47'}}>Project</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold" style={{color: '#3A3F47'}}>Category</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold" style={{color: '#3A3F47'}}>Location</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold" style={{color: '#3A3F47'}}>Budget (₹)</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold" style={{color: '#3A3F47'}}>Utilized</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold" style={{color: '#3A3F47'}}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {csrProjects.slice(0, 5).map((project) => (
                    <tr key={project.id} style={{borderBottom: '1px solid #F8F7F4'}} className="hover:bg-slate-50">
                      <td className="py-2 px-3 text-xs font-medium" style={{color: '#3A3F47'}}>{project.name}</td>
                      <td className="py-2 px-3 text-xs" style={{color: '#6B6B6B'}}>{project.category}</td>
                      <td className="py-2 px-3 text-xs" style={{color: '#6B6B6B'}}>
                        <div className="flex items-center">
                          <MapPin size={12} className="mr-1" style={{color: '#D1A85B'}} />
                          {project.location}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-xs text-right" style={{color: '#3A3F47'}}>
                        {(project.budget / 1000000).toFixed(2)}M
                      </td>
                      <td className="py-2 px-3 text-xs text-right" style={{color: '#6B6B6B'}}>
                        {(project.budget * project.utilization / 100 / 1000000).toFixed(2)}M
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium text-white" style={{
                          background: project.status === 'active' ? '#D1A85B' : '#6B6B6B'
                        }}>
                          {project.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* AI Assistant */}
          <div data-testid="ai-assistant" className="rounded-xl shadow-sm border p-5 mb-6" style={{background: '#3A3F47', borderColor: '#D1A85B'}}>
            <h3 className="text-lg font-semibold text-white mb-3" style={{fontFamily: 'Poppins, sans-serif'}}>AI-Powered Insights</h3>
            <p className="mb-3 text-sm" style={{color: '#E0DED9'}}>Ask questions about your CSR and ESG data</p>
            <div className="flex gap-3 mb-3">
              <Input
                data-testid="ai-query-input"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder="e.g., What is our highest performing ESG category?"
                className="flex-1 bg-white text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleAIQuery()}
              />
              <Button
                data-testid="ai-query-btn"
                onClick={handleAIQuery}
                disabled={aiLoading}
                className="px-5 text-sm"
                style={{background: '#D1A85B', color: '#FFFFFF'}}
              >
                {aiLoading ? 'Processing...' : <Send size={16} />}
              </Button>
            </div>
            {aiResponse && (
              <div data-testid="ai-response" className="rounded-lg p-3 text-sm" style={{background: 'rgba(209, 168, 91, 0.1)', borderLeft: '3px solid #D1A85B'}}>
                <p className="text-white">{aiResponse}</p>
              </div>
            )}
          </div>

          {/* Stakeholder Sentiments */}
          <div data-testid="sentiments-section" className="bg-white rounded-xl shadow-sm border p-5" style={{borderColor: '#E0DED9'}}>
            <h3 className="text-lg font-semibold mb-3" style={{fontFamily: 'Poppins, sans-serif', color: '#3A3F47'}}>Recent Stakeholder Feedback</h3>
            <div className="space-y-3">
              {sentiments.slice(0, 5).map((sentiment) => (
                <div key={sentiment.id} className="border-l-4 pl-3 py-2" style={{borderColor: '#D1A85B'}}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium" style={{color: '#3A3F47'}}>{sentiment.source}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{
                      background: sentiment.sentiment === 'positive' 
                        ? '#D1A85B'
                        : sentiment.sentiment === 'negative'
                        ? '#3A3F47'
                        : '#6B6B6B'
                    }}>
                      {sentiment.sentiment}
                    </span>
                  </div>
                  <p className="text-xs mb-1" style={{color: '#6B6B6B'}}>{sentiment.text}</p>
                  <div className="flex gap-2">
                    {sentiment.topics.map((topic, idx) => (
                      <span key={idx} className="text-xs px-2 py-0.5 rounded" style={{background: '#F8F7F4', color: '#D1A85B'}}>
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpactDashboard;
